import { Ban } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { formatHour } from '../../lib/format';

/**
 * "Add Block" dialog for closing a court range (maintenance, private event, etc.).
 * Extracted from OperatorToday. Preserves pre-selected cell shortcut UX.
 */
export default function BlockCourtDialog({
  open,
  onOpenChange,
  selectedCell,        // { courtName, hour } | null — when opened from a specific tile
  selectedDate,
  courts,
  hours,
  courtId,
  startHour,
  endHour,
  reason,
  onCourtIdChange,
  onStartHourChange,
  onEndHourChange,
  onReasonChange,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="block-court-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Add Block</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {selectedCell ? (
            <div className="text-sm text-text-secondary bg-app-bg rounded-xl p-3">
              {selectedCell.courtName} · Starting {formatHour(selectedCell.hour)} · {selectedDate}
            </div>
          ) : (
            <>
              <div>
                <Label className="text-sm font-medium text-text-secondary">Court</Label>
                <Select value={courtId} onValueChange={onCourtIdChange}>
                  <SelectTrigger className="rounded-xl mt-1.5 border-border-subtle" data-testid="block-court-select">
                    <SelectValue placeholder="Select court" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Courts</SelectItem>
                    {courts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-text-secondary">Start Time</Label>
                <Select value={startHour} onValueChange={onStartHourChange}>
                  <SelectTrigger className="rounded-xl mt-1.5 border-border-subtle" data-testid="block-start-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {hours.map(h => (
                      <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label className="text-sm font-medium text-text-secondary">Block through</Label>
            <Select value={endHour} onValueChange={onEndHourChange}>
              <SelectTrigger className="rounded-xl mt-1.5 border-border-subtle" data-testid="block-end-select">
                <SelectValue placeholder="End time" />
              </SelectTrigger>
              <SelectContent>
                {hours.filter(h => {
                  const start = selectedCell?.hour ?? parseInt(startHour);
                  return !isNaN(start) && h >= start;
                }).map(h => (
                  <SelectItem key={h} value={String(h + 1)}>{formatHour(h)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium text-text-secondary">Reason (optional)</Label>
            <Input
              data-testid="block-reason-input"
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="e.g. Maintenance, Private event, Cleaning"
              className="rounded-xl mt-1.5 border-border-subtle focus:border-coral"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            data-testid="block-submit-btn"
            onClick={onSubmit}
            className="bg-coral hover:bg-coral/90 text-white rounded-xl px-8"
          >
            <Ban className="w-4 h-4 mr-2" /> Close Court
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
